import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ObjectMap } from 'mf_core/config';
import { useGetWidgetDetails } from '../hooks/use-insightsWidget';
import InsightsContent from './internal/InsightsContent';
import { useLocalizationContext } from '@im/react-localization';
import { modifyDigitalData } from 'mf_core/adobe';
import { GetUserRole, userRoles } from '../utils/common.util';
import { useFeatureFlag } from 'mf_core/feature-flag';

type Props = {
  hasInsightsData: boolean | null;
  isInsightsButtonClicked?: boolean;
  isInsightsDropOpen?: boolean;
  onClickInsightsCenter: (navigateLink?: string) => void;
  updateInsightDataStatus?: (hasData: boolean | null) => void;
};

export default function QuickInsights(props: Readonly<Props>) {
  const [widgetKey, setWidgetKey] = useState<number>();
  const [filterKey, setFilterKey] = useState<string>('');
  const [insightLoader, setInsightLoader] = useState(false);
  const [insightError, setInsightError] = useState(false);
  const [insightsData, setInsightsData] = useState<Array<ObjectMap>>([]);
  const [additionalInsightsData, setAdditionalInsightsData] =
    useState<ObjectMap>({});

  const [currentPage, setCurrentPage] = useState('');
  const [widgetTitle, setWidgetTitle] = useState('');
  const widgetMutation = useGetWidgetDetails();
  const location = useLocation();
  const { locale } = useLocalizationContext();
  const { value: enableUserManagement } = useFeatureFlag(
    'EnableUserManagement'
  );

  const widgetKeys: ObjectMap = {
    '/insight/insights-v2': 2,
    '/invoice/InvoiceList': 59,
    '/invoice/invoicessearch': 59,
    '/my/cart': 6,
    '/my/dashboard': 1,
    '/my/ordertracking/v3/orderdetails': 58,
    '/my/ordertracking/v3/ordersearch': 58,
    '/my/subscriptions/list': 61,
    '/product/productdetails': 7,
    '/product/productsearch': 3,
    '/product/subscriptioncategories': 5,
  };

  const pageNames: ObjectMap = {
    '/insight/insights-v2': 'Insights Center',
    '/invoice/InvoiceList': 'Invoice Page',
    '/invoice/invoicessearch': 'Invoice Page',
    '/my/cart': 'cart checkout',
    '/my/dashboard': 'Home Page',
    '/my/ordertracking/v3/orderdetails': 'Order Page',
    '/my/ordertracking/v3/ordersearch': 'Order Page',
    '/my/subscriptions/list': 'Subscription Page',
    '/product/productdetails': 'Details Page',
    '/product/productsearch': 'Search Page',
  };

  const enableInsightsPaths = [
    '/insight/insights-v2',
    '/my/cart',
    '/my/dashboard',
    '/product/productsearch',
    '/product/productdetails',
    '/product/subscriptioncategories',
    '/frame',
    '/my/ordertracking/v3/ordersearch',
    '/my/ordertracking/v3/orderdetails',
    '/invoice/InvoiceList',
    '/invoice/invoicessearch',
    '/my/subscriptions/list',
  ];

  const userRole = GetUserRole();

  const getUrlParams = useCallback((queryString: string) => {
    const vendorName = new URLSearchParams(queryString).get('vendor') ?? '';
    return vendorName;
  }, []);

  const updateInsightsDetails = useCallback((data: ObjectMap[]) => {
    return data.map((insight: ObjectMap) => {
      const updatedInsightContent = insight.insightContent.replace(
        /&gt;([^&]*)&lt;\/a/g,
        (match: string, group: string) => {
          return decodeURIComponent(match);
        }
      );
      return {
        ...insight,
        insightContent: updatedInsightContent,
      };
    });
  }, []);

  const updateVisitedPagesData = useCallback(
    (pathName: string) => {
      const visitedPages = JSON.parse(
        localStorage.getItem('visitedQuickInsightPages') ?? '[]'
      );

      if (!visitedPages.includes(location?.pathname)) {
        if (
          pathName === '/my/ordertracking/v3/ordersearch' ||
          pathName === '/my/ordertracking/v3/orderdetails' ||
          (pathName === '/frame' && location.search.includes('ordersearch'))
        ) {
          visitedPages.push('/my/ordertracking/');
        } else visitedPages.push(location?.pathname);

        localStorage.setItem(
          'visitedQuickInsightPages',
          JSON.stringify(visitedPages)
        );
      }
    },
    [location]
  );

  useEffect(() => {
    const pathName = location?.pathname ?? '';
    const timer: any = null;

    if (enableInsightsPaths.includes(pathName) && props.isInsightsDropOpen) {
      if (
        (pathName === '/product/productsearch' &&
          (location?.search?.includes('categories') ||
            location?.search?.includes('category'))) ||
        pathName === '/product/subscriptioncategories'
      ) {
        setWidgetKey(5);
        setCurrentPage('Product Category');
        setFilterKey('');
      } else if (
        pathName === '/product/productsearch' &&
        location?.search?.toLowerCase().includes('vendor')
      ) {
        setWidgetKey(4);
        setCurrentPage('Vendor List');

        const vendor: any = getUrlParams(location?.search);
        setFilterKey(`vendorname:${vendor}`);
      } else if (
        (pathName === '/frame' && location.search.includes('ordersearch')) ||
        pathName === '/my/ordertracking/v3/ordersearch' ||
        pathName === '/my/ordertracking/v3/orderdetails'
      ) {
        setWidgetKey(58);
        setCurrentPage('Order Page');
        setFilterKey('');
      } else if (pathName === '/frame') {
        setWidgetKey(57);
        setCurrentPage('Renewals Page');
        setFilterKey('');
      } else if (
        pathName === '/invoice/invoicessearch' ||
        pathName === '/invoice/InvoiceList'
      ) {
        setWidgetKey(59);
        setCurrentPage('Invoice Page');
        setFilterKey('');
      } else {
        setWidgetKey(widgetKeys[pathName]);
        setCurrentPage(pageNames[pathName]);
        setFilterKey('');
      }

      updateVisitedPagesData(pathName);
    }

    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isInsightsDropOpen, location]);

  useEffect(() => {
    const pathName = location?.pathname ?? '';
    if (enableInsightsPaths.includes(pathName)) setInsightLoader(true);
    modifyDigitalData('QuickInsightsImpressions');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.pathname]);

  useEffect(() => {
    if (widgetKey) {
      setInsightLoader(true);
      widgetMutation
        .mutateAsync({ filters: filterKey, widgetKey: widgetKey })
        .then((response) => {
          const htmlData = JSON.parse(
            response?.insights?.[0]?.insightContent?.[0]?.insightBodyContent
              ?.html ?? '[]'
          );

          const data = JSON.parse(
            response?.insights?.[0]?.insightContent?.[0]?.insightBodyContent
              ?.data ?? '{}'
          );

          let updatedData = updateInsightsDetails(htmlData);
          setAdditionalInsightsData(data);

          if (enableUserManagement === 'true' && userRole === userRoles.BASIC) {
            updatedData = updatedData?.filter((InsightCard: ObjectMap) => {
              return InsightCard?.dataName !== 'SalesTrend';
            });
          }

          setInsightsData(updatedData);

          if (props.updateInsightDataStatus) {
            let dataStatus: boolean | null = true;

            if (props?.isInsightsButtonClicked) {
              dataStatus = Boolean(updatedData?.length);
            } else if (updatedData.length === 0) dataStatus = null;

            props.updateInsightDataStatus(dataStatus);
          }

          setWidgetTitle(response?.widgetTitle);
        })
        .catch((error: any) => {
          setInsightsData([]);
          if (error) setInsightError(true);

          if (props.updateInsightDataStatus)
            props.updateInsightDataStatus(
              props?.isInsightsButtonClicked ? false : null
            );
        })
        .finally(() => {
          setInsightLoader(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgetKey, filterKey, locale]);

  if (props?.hasInsightsData !== null)
    return (
      <InsightsContent
        additionalInsightsData={additionalInsightsData}
        currentPage={currentPage}
        insightsData={insightsData}
        isInsightsError={insightError}
        isInsightsLoading={insightLoader}
        onClickInsightsCenter={props?.onClickInsightsCenter}
        widgetTitle={widgetTitle}
      />
    );
}
